/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { getLocale } from 'umi';

/*
 * Capitalize(text)
 *
 * Converts the first character of a text to capital (uppercase) letter and remaining characters to lowercase.
 *
 * SAMPLES
 * Capitalize("hello world") ->
 * Capitalize("Hi All") Hi all
 *
 * INPUT
 * text: The string to be converted.
 *
 * OUTPUT
 * Returns the text with the first character made uppercase.
 */
function Capitalize(text) {
  return text === null || text === undefined || text.length === 0
    ? ''
    : text.charAt(0).toUpperCase() + text.slice(1);
}

/*
 * Concat(value, …)
 *
 * This function returns a concatenated string of several items that had been used as parameters
 *
 * SAMPLES
 * Concat("$",@amount) -> $10
 * Concat("New"," ","York") -> New York
 *
 * INPUT
 * values: The strings to concat.
 *
 * OUTPUT
 * Returns a concatenated string with the values used as parameters
 */
function Concat(...values) {
  if (values === null || values === undefined || values.length === 0) return '';
  return values.join('');
}

/*
 * Contains(value, search)
 *
 * Checks if a string contains the search value
 *
 * SAMPLES
 * Contains("Hello there!", "there") -> True
 * Contains("Hello there!", "there?") -> False
 *
 * INPUT
 * value: The string to be checked.
 * search: The term to be searched in
 *
 * OUTPUT
 * Returns True if the search term is contained in value, otherwise False
 */
function Contains(value, search) {
  return value === null ||
    value === undefined ||
    value.length === 0 ||
    search === null ||
    search === undefined ||
    search.length === 0
    ? false
    : value.includes(search);
}

/*
 * Length(value)
 *
 * Gets the length of the given string
 *
 * SAMPLES
 * Length("Hi there!") -> 9
 *
 * INPUT
 * value: The string to be checked
 *
 * OUTPUT
 * returns the length of characters
 */
function Length(value) {
  return value == null ? 0 : String(value).length;
}

/*
 * Lower(text)
 *
 * Converts the text to all lowercase letters
 *
 * SAMPLES
 * Lower("Hi ALL") -> hi all
 *
 * INPUT
 * text: The string to be converted to lowercase
 *
 * OUTPUT
 * Returns the text with all characters made lowercase
 */
function Lower(text) {
  return text === null || text === undefined || text.length === 0 ? '' : _.toLower(text);
}

/*
 * Upper(text)
 *
 * Converts the text to all uppercase letters.
 *
 * SAMPLES
 * Upper("Hi all!") -> HI ALL!
 *
 * INPUT
 * text: The string to be converted to uppercase.
 *
 * OUTPUT
 * Returns the text with all characters made uppercase.
 */
function Upper(text) {
  return text === null || text === undefined || text.length === 0 ? '' : _.toUpper(text);
}

/*
 * Replace(text, old, new)
 *
 * Replaces all occurrences of a substring within a text, with a new substring
 *
 * SAMPLES
 * Replace("Hello!", "llo", "y") -> "Hey!"
 * Replace("hahaha", "a", "e") -> "hehehe"
 *
 * INPUT
 * text: The original string
 * old: The string to be replaced
 * new: The new replacement string
 *
 * OUTPUT
 * Returns a new string with all matches of old substring replaced by the new substring
 */
function Replace(text, old, replaceValue) {
  if (text === null || text === undefined) return '';
  if (old === null || old === undefined || old.length === 0) return text;
  return text.replace(old, replaceValue);
}

/*
 * Slice(value, start, end)
 *
 * Extracts some consecutive elements from an string
 *
 * SAMPLES
 * Slice("Hello world", 4, 8) -> "o wo"
 * Slice("Hello world", 4, -1) -> "o worl"
 * Slice("Hello world", 4) -> "o world"
 *
 * INPUT
 * value: The string to be processed
 * start: Optional.The position to start from.Counts from 0.
 * end: Optional.The position to end at.Counts from 0.
 *
 * OUTPUT
 * Returns a part of the provided value based on the start and end positions
 */
function Slice(value, start, end) {
  if (value == null || value.length === 0) return '';
  return Number.isNaN(value) ? value.slice(start, end) : value.toString().slice(start, end);
}

/*
 * Title(text)
 *
 * Converts the first character in each word to capital (uppercase) letter and remaining characters to lowercase
 *
 * Title("hello world") -> Hello World
 * Title("MY friend") -> My Friend
 *
 * INPUT
 * text: The string to be converted to title
 *
 * OUTPUT
 * Returns the text with first characters of all the words made capitalized
 */
function Title(text) {
  if (text === null || text === undefined || text.length === 0) return '';
  const sentence = text.toLowerCase().split(' ');
  for (let i = 0; i < sentence.length; i++) {
    sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1);
  }
  return sentence.join(' ');
}

/*
 * PadStart(text, targetLength, padString)
 *
 * The method pads the current string with another string (multiple times, if needed) until the resulting string reaches the given length
 *
 * PadStart("5", 4, "0") -> "0005"
 * PadStart("0980", 5, "0") -> "00980"
 * PadStart("0012345", 8, "CE") -> "CEC0012345"
 *
 * INPUT
 * text: The string to apply PadStart
 * targetLength: The length of the resulting string once the current str has been padded. If the value is less than str.length, then str is returned as-is.
 * padString: The string to pad the current str with. If padString is too long to stay within the targetLength, it will be truncated from the end
 *
 * OUTPUT
 * A String of the specified targetLength with padString applied from the start
 */
function PadStart(text, targetLength, padString) {
  if (text == null) text = '';
  return targetLength === 0 ? text : text.toString().padStart(targetLength, padString);
}

/*
 * PadEnd(text, targetLength, padString)
 *
 * The method pads the current string with another string (multiple times, if needed) until the resulting string reaches the given length
 *
 * PadEnd("5", 4, "0") -> "5000"
 * PadEnd("0980", 5, "0") -> "09800"
 * PadEnd("0012345", 8, "CE") -> "0012345CEC"
 *
 * INPUT
 * text: The string to apply PadEnd
 * targetLength: The length of the resulting string once the current str has been padded. If the value is less than str.length, then str is returned as-is.
 * padString: The string to pad the current str with. If padString is too long to stay within the targetLength, it will be truncated from the end
 *
 * OUTPUT
 * A String of the specified targetLength with padString applied from the end
 */
function PadEnd(text, targetLength, padString) {
  if (text == null) text = '';
  return targetLength === 0 ? text : text.toString().padEnd(targetLength, padString);
}

/*
 * ToString(value)
 *
 * Converts a value to text.
 *
 * ToString(10.45) -> "10.45"
 * ToString(Concat(1233174,'-',323147.16)) -> "1233174-323147.16"
 *
 * INPUT
 * value: The value to be converted to string.
 *
 * OUTPUT
 * Returns the value as text.
 */
function ToString(value) {
  return String(value);
}

/*
 * NumberToWordsV2(value, language, decimalsMode)
 * Converts a number to words using supported languages.
 *
 * INPUT
 *
 * value: Number to convert.
 * language: Optional language code ("es", "en", "pt"). Uses current locale if omitted.
 * decimalsMode: Optional ("Words", "Digits", "WordsAndDigits"). Default is "Words".
 *
 * Optimized version of NumberToWords.
 * Improvements over V1:
 *   - Unified below20 array (0–19) replaces separate ones + teens arrays
 *   - English hundreds derived dynamically: below20[i] + ' hundred' (no hardcoded list)
 *   - Single NTW_CONFIGS object replaces getLanguageWords() if-else chain (O(1) lookup)
 *   - All V1 utility helpers (parseDecimalValue, normalizeLanguage, normalizeDecimalsMode) reused
 *
 * NumberToWords(1050, "es") -> "mil cincuenta"
 * NumberToWords(99.55, "en", "Words") -> "ninety nine with fifty five"
 * NumberToWords(99.55, "pt", "Digits") -> "noventa e nove com 55/100"
 *
 * OUTPUT
 * Returns number in words.
 */

const NTW_CONFIGS = {
  en: {
    below20: [
      'zero',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ],
    tens: ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'],
    hundredWord: 'hundred', // hundreds derived: below20[i] + ' ' + hundredWord
    scales: ['', 'thousand', 'million', 'billion', 'trillion', 'quadrillion'],
    tensConnector: '',
    decimalConnector: 'with',
  },
  es: {
    below20: [
      'cero',
      'uno',
      'dos',
      'tres',
      'cuatro',
      'cinco',
      'seis',
      'siete',
      'ocho',
      'nueve',
      'diez',
      'once',
      'doce',
      'trece',
      'catorce',
      'quince',
      'dieciseis',
      'diecisiete',
      'dieciocho',
      'diecinueve',
    ],
    tens: [
      '',
      'diez',
      'veinte',
      'treinta',
      'cuarenta',
      'cincuenta',
      'sesenta',
      'setenta',
      'ochenta',
      'noventa',
    ],
    hundreds: [
      '',
      'ciento',
      'doscientos',
      'trescientos',
      'cuatrocientos',
      'quinientos',
      'seiscientos',
      'setecientos',
      'ochocientos',
      'novecientos',
    ],
    exactHundred: 'cien',
    scales: ['', 'mil', 'millon', 'billon', 'trillon', 'cuatrillon'],
    scalesPlural: ['', 'mil', 'millones', 'billones', 'trillones', 'cuatrillones'],
    tensConnector: 'y',
    decimalConnector: 'con',
  },
  pt: {
    below20: [
      'zero',
      'um',
      'dois',
      'tres',
      'quatro',
      'cinco',
      'seis',
      'sete',
      'oito',
      'nove',
      'dez',
      'onze',
      'doze',
      'treze',
      'catorze',
      'quinze',
      'dezesseis',
      'dezessete',
      'dezoito',
      'dezenove',
    ],
    tens: [
      '',
      'dez',
      'vinte',
      'trinta',
      'quarenta',
      'cinquenta',
      'sessenta',
      'setenta',
      'oitenta',
      'noventa',
    ],
    hundreds: [
      '',
      'cento',
      'duzentos',
      'trezentos',
      'quatrocentos',
      'quinhentos',
      'seiscentos',
      'setecentos',
      'oitocentos',
      'novecentos',
    ],
    exactHundred: 'cem',
    scales: ['', 'mil', 'milhao', 'bilhao', 'trilhao', 'quadrilhao'],
    scalesPlural: ['', 'mil', 'milhoes', 'bilhoes', 'trilhoes', 'quadrilhoes'],
    tensConnector: 'e',
    decimalConnector: 'com',
  },
};

/*
 * ntwGetHundredWord(config, hundredIndex)
 *
 * Returns the word for a hundreds-place value (e.g. 200, 500).
 * For EN: derives it dynamically as below20[i] + ' hundred'.
 * For ES/PT: reads from the precomputed config.hundreds array (irregular forms).
 *
 * INPUT
 * config: NTW_CONFIGS language entry
 * hundredIndex: integer 0–9 (hundreds digit of the current chunk)
 *
 * OUTPUT
 * Returns the hundreds word, or '' when hundredIndex is 0.
 */
function ntwGetHundredWord(config, hundredIndex) {
  if (hundredIndex === 0) return '';
  return config.hundredWord
    ? `${config.below20[hundredIndex]} ${config.hundredWord}`
    : config.hundreds[hundredIndex];
}

/*
 * ntwGetExactHundred(config)
 *
 * Returns the word for exactly 100. Some languages use a distinct form
 * when the value is an exact hundred (e.g. ES "cien" vs "ciento", PT "cem" vs "cento").
 * For EN: derived as below20[1] + ' hundred' → "one hundred".
 *
 * INPUT
 * config: NTW_CONFIGS language entry
 *
 * OUTPUT
 * Returns the exact-hundred word string.
 */
function ntwGetExactHundred(config) {
  return config.hundredWord ? `${config.below20[1]} ${config.hundredWord}` : config.exactHundred;
}

/*
 * ntwConvertChunk(chunk, config)
 *
 * Converts a 3-digit integer (0–999) into its word representation for the given language.
 * Handles ones (0–9), teens (10–19), tens (20–99), and hundreds (100–999) including
 * the language-specific tens connector (e.g. ES "y", PT "e", EN "").
 *
 * SAMPLES
 * ntwConvertChunk(0, enConfig)   -> ''
 * ntwConvertChunk(15, enConfig)  -> 'fifteen'
 * ntwConvertChunk(42, esConfig)  -> 'cuarenta y dos'
 * ntwConvertChunk(100, ptConfig) -> 'cem'
 * ntwConvertChunk(215, enConfig) -> 'two hundred fifteen'
 *
 * INPUT
 * chunk:  Integer 0–999
 * config: NTW_CONFIGS language entry
 *
 * OUTPUT
 * Returns the word string, or '' when chunk is 0.
 */
function ntwConvertChunk(chunk, config) {
  if (chunk === 0) return '';
  if (chunk < 20) return config.below20[chunk];
  if (chunk < 100) {
    const ten = Math.floor(chunk / 10);
    const one = chunk % 10;
    if (one === 0) return config.tens[ten];
    const connector = config.tensConnector ? ` ${config.tensConnector} ` : ' ';
    return `${config.tens[ten]}${connector}${config.below20[one]}`;
  }

  const hundred = Math.floor(chunk / 100);
  const rest = chunk % 100;

  if (rest === 0 && hundred === 1) return ntwGetExactHundred(config);
  if (rest === 0) return ntwGetHundredWord(config, hundred);

  return `${ntwGetHundredWord(config, hundred)} ${ntwConvertChunk(rest, config)}`;
}

/*
 * ntwConvertInteger(number, config, language)
 *
 * Converts a non-negative integer of any size into words by splitting it into
 * 3-digit chunks and applying the appropriate scale word (thousand, million, …).
 * Handles pluralization via config.scalesPlural (ES/PT), and language-specific
 * special cases (ES "uno mil" → "un mil", PT "um mil" → "mil").
 *
 * SAMPLES
 * ntwConvertInteger(0,       enConfig, 'en') -> 'zero'
 * ntwConvertInteger(1000,    esConfig, 'es') -> 'mil'
 * ntwConvertInteger(9450460, esConfig, 'es') -> 'nueve millones cuatrocientos cincuenta mil cuatrocientos sesenta'
 *
 * INPUT
 * number:   Non-negative integer
 * config:   NTW_CONFIGS language entry
 * language: Normalized language code ('en' | 'es' | 'pt')
 *
 * OUTPUT
 * Returns the full integer as a word string.
 */
function ntwConvertInteger(number, config, language) {
  if (number === 0) return config.below20[0];

  const parts = [];
  let value = number;
  let scaleIndex = 0;

  while (value > 0) {
    const chunk = value % 1000;
    if (chunk > 0) {
      const chunkWords = ntwConvertChunk(chunk, config);
      if (scaleIndex === 1 && chunk === 1) parts.unshift(config.scales[1]);
      else if (scaleIndex > 0) {
        const scaleArr = chunk > 1 && config.scalesPlural ? config.scalesPlural : config.scales;
        const scale = scaleArr[scaleIndex] || scaleArr[scaleArr.length - 1] || '';
        parts.unshift(`${chunkWords} ${scale}`.trim());
      } else parts.unshift(chunkWords);
    }
    value = Math.floor(value / 1000);
    scaleIndex += 1;
  }

  let result = parts.join(' ').trim();
  if (language === 'es' && result.toLowerCase().startsWith('uno mil')) {
    result = `un${result.substring(3)}`;
  }

  if (language === 'pt' && result.toLowerCase().startsWith('um mil')) {
    result = result.substring(3).trimStart();
  }

  return result;
}

/*
 * parseDecimalValue(value)
 *
 * Normalizes and parses any input into a finite JavaScript number.
 * Handles numeric types directly. Parses strings with mixed separators:
 * detects whether ',' or '.' is the decimal separator by position,
 * strips thousands separators, and converts to a standard float.
 *
 * SAMPLES
 * parseDecimalValue(99.5)        -> 99.5
 * parseDecimalValue('1.234,56')  -> 1234.56   (ES/PT format)
 * parseDecimalValue('1,234.56')  -> 1234.56   (EN format)
 * parseDecimalValue(null)        -> null
 * parseDecimalValue(Infinity)    -> null
 *
 * INPUT
 * value: Any value to parse
 *
 * OUTPUT
 * Returns the parsed finite number, or null if unparseable.
 */
function parseDecimalValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const text = String(value).trim();
  if (text.length === 0) return null;

  let normalized = text.replace(/\s/g, '');
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    if (decimalSeparator === ',') normalized = normalized.replace(/\./g, '').replace(',', '.');
    else normalized = normalized.replace(/,/g, '');
  } else if (hasComma) normalized = normalized.replace(',', '.');

  const result = Number(normalized);
  return Number.isFinite(result) ? result : null;
}

/*
 * normalizeLanguage(language)
 *
 * Normalizes a language code to one of the three supported values: 'en', 'es', 'pt'.
 * Strips region tags (e.g. 'en-US' → 'en', 'pt_BR' → 'pt').
 * Falls back to the browser/system locale when language is absent,
 * and defaults to 'es' for any unsupported locale.
 *
 * SAMPLES
 * normalizeLanguage('en-US') -> 'en'
 * normalizeLanguage('PT_BR') -> 'pt'
 * normalizeLanguage(null)    -> 'es' (or detected locale)
 * normalizeLanguage('fr')    -> 'es' (unsupported, defaults to ES)
 *
 * INPUT
 * language: Any language string, or null/undefined
 *
 * OUTPUT
 * Returns 'en', 'es', or 'pt'.
 */
function normalizeLanguage(language) {
  let code = language;
  if (code === null || code === undefined || String(code).trim().length === 0) {
    code = Intl.DateTimeFormat().resolvedOptions().locale || 'es';
  }

  let normalized = String(code).trim().toLowerCase();
  if (normalized.includes('-') || normalized.includes('_')) {
    normalized = normalized.split(/[-_]/)[0];
  }

  if (normalized === 'en' || normalized === 'pt') return normalized;
  return 'es';
}

/*
 * normalizeDecimalsMode(decimalsMode)
 *
 * Normalizes the decimalsMode parameter to a lowercase internal token.
 * Case-insensitive: 'Words', 'WORDS', 'words' all resolve to 'words'.
 *
 * SAMPLES
 * normalizeDecimalsMode('Words')         -> 'words'
 * normalizeDecimalsMode('Digits')        -> 'digits'
 * normalizeDecimalsMode('WordsAndDigits')-> 'wordsanddigits'
 * normalizeDecimalsMode(null)            -> 'words'
 *
 * INPUT
 * decimalsMode: 'Words' | 'Digits' | 'WordsAndDigits', or null/undefined
 *
 * OUTPUT
 * Returns 'words', 'digits', or 'wordsanddigits'.
 */
function normalizeDecimalsMode(decimalsMode) {
  if (decimalsMode === null || decimalsMode === undefined) return 'words';
  const normalized = String(decimalsMode).trim().toLowerCase();
  if (normalized === 'digits') return 'digits';
  if (normalized === 'wordsanddigits') return 'wordsanddigits';
  return 'words';
}

function NumberToWords(value, language, decimalsMode) {
  const parsedValue = parseDecimalValue(value);
  if (parsedValue === null) {
    return '';
  }

  const lang = normalizeLanguage(language);
  const mode = normalizeDecimalsMode(decimalsMode);
  const config = NTW_CONFIGS[lang];

  const isNegative = parsedValue < 0;
  const absoluteValue = Math.abs(parsedValue);
  const integerPart = Math.trunc(absoluteValue);

  let decimalValue = Math.round((absoluteValue - integerPart) * 100);
  let integerValue = integerPart;

  if (decimalValue === 100) {
    integerValue += 1;
    decimalValue = 0;
  }

  const integerWords = ntwConvertInteger(integerValue, config, lang);
  if (decimalValue <= 0) return isNegative ? `-${integerWords}` : integerWords;

  const decimalWords = ntwConvertInteger(decimalValue, config, lang);
  const decimalDigits = `${decimalValue.toString().padStart(2, '0')}/100`;

  let result;
  if (mode === 'digits') result = `${integerWords} ${config.decimalConnector} ${decimalDigits}`;
  else if (mode === 'wordsanddigits') {
    result = `${integerWords} ${config.decimalConnector} ${decimalWords} (${decimalDigits})`;
  } else result = `${integerWords} ${config.decimalConnector} ${decimalWords}`;

  return isNegative ? `-${result}` : result;
}

export default {
  Capitalize,
  Concat,
  Contains,
  Length,
  Lower,
  Upper,
  Replace,
  Slice,
  Title,
  PadStart,
  PadEnd,
  ToString,
  NumberToWords: (value, language, mode) => {
    return NumberToWords(value, language ?? getLocale(), mode);
  },
};
